/**
 * Warehouse Stock Alert Service (Ideariso D14 — avvisi scorta)
 *
 * Ascolta in realtime gli INSERT su stock_movements (che il trigger DB
 * update_stock_after_movement_trigger già usa per aggiornare products.stock_quantity
 * su OGNI flusso reale: entrata merce, uscita merce, produzione, rettifica manuale —
 * verificato su prod, unico punto davvero centralizzato) e notifica gli utenti con
 * permesso di gestione magazzino quando lo stock risultante scende sotto
 * products.low_stock_threshold.
 *
 * Nota: la soglia è a livello di PRODOTTO, non per zona/magazzino — product_zone_storage
 * (l'unica cosa "per zona" nel modello dati) viene aggiornato solo dalle modifiche
 * manuali di stoccaggio in ProductDetail, non dai flussi DDT/produzione, quindi una
 * soglia per-zona non rifletterebbe l'uso reale (deciso con Luca il 26/07/2026).
 */
import { supabase } from '../supabase';

class WarehouseStockAlertService {
  private channel: any = null;
  private organizationId: string | null = null;
  private recentlyAlerted = new Map<string, number>();
  private readonly cooldownMs = 60 * 60 * 1000; // 1h: evita spam su movimenti ravvicinati sullo stesso prodotto

  async init(organizationId: string) {
    if (this.channel && this.organizationId === organizationId) return;
    if (this.channel) await this.disconnect();

    this.organizationId = organizationId;
    this.channel = supabase
      .channel(`warehouse_stock_alert_${organizationId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'stock_movements', filter: `organization_id=eq.${organizationId}` },
        (payload: any) => this.handleMovementInsert(payload)
      )
      .subscribe();
  }

  async disconnect() {
    if (this.channel) {
      await supabase.removeChannel(this.channel);
      this.channel = null;
    }
    this.organizationId = null;
  }

  private async handleMovementInsert(payload: any) {
    try {
      const movement = payload?.new;
      if (!movement || movement.variation_id || movement.new_stock == null) return;

      const productId = movement.product_id as string;
      const newStock = movement.new_stock as number;

      const last = this.recentlyAlerted.get(productId);
      if (last && Date.now() - last < this.cooldownMs) return;

      const { data: product } = await supabase
        .from('products')
        .select('name, sku, low_stock_threshold')
        .eq('id', productId)
        .single();

      if (!product?.low_stock_threshold || product.low_stock_threshold <= 0) return;
      if (newStock >= product.low_stock_threshold) return;

      this.recentlyAlerted.set(productId, Date.now());
      await this.notifyLowStock(productId, product.name, product.sku, newStock, product.low_stock_threshold);
    } catch (error) {
      console.error('[WarehouseStockAlertService] Error handling movement insert:', error);
    }
  }

  private async notifyLowStock(productId: string, productName: string, sku: string | undefined, currentStock: number, threshold: number) {
    if (!this.organizationId) return;

    const { data: userRoles } = await supabase
      .from('user_roles')
      .select('user_id, role:roles(role_permissions(permission:permissions(resource, action)))')
      .eq('organization_id', this.organizationId);

    const recipients = new Set<string>();
    (userRoles || []).forEach((ur: any) => {
      const perms = ur.role?.role_permissions || [];
      const canManageInventory = perms.some((rp: any) => rp.permission?.resource === 'inventory' && rp.permission?.action === 'update');
      if (canManageInventory) recipients.add(ur.user_id);
    });
    if (recipients.size === 0) return;

    const notifications = Array.from(recipients).map(userId => ({
      user_id: userId,
      organization_id: this.organizationId,
      event_type: 'warehouse_low_stock',
      type: 'warning',
      title: 'Scorta sotto soglia',
      message: `${productName}${sku ? ` (${sku})` : ''}: ${currentStock} in stock, sotto la soglia minima di ${threshold}`,
      link: `/magazzino/prodotti/${productId}`,
      entity_type: 'product',
      entity_id: productId,
      priority: 'high',
      metadata: { current_stock: currentStock, threshold },
      read: false,
      delivered_via: ['in_app'],
    }));

    const { error } = await supabase.from('notifications').insert(notifications);
    if (error) console.error('[WarehouseStockAlertService] Notification insert error:', error);
  }
}

export const warehouseStockAlertService = new WarehouseStockAlertService();
